import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import { FieldValues, SubmitHandler } from "react-hook-form";
import { ApiResponse } from "src/apis";
import { useSnackbar } from "src/context/ShowMessage";
import { useAuth } from "src/auth/AuthContext";

interface useHandleSubmitProps {
  id?: number;
  // isConvert?: boolean;
  addApiFunction: (values: any) => Promise<any>;
  updateApiFunction: (id: number, values: any) => Promise<ApiResponse>;
  moduleNameKey: string;
  callBackFn?: (data: any) => void;
  conflictFn?: (data: any) => void;
}

const useHandleSubmit = <T extends FieldValues>({
  id,
  addApiFunction,
  updateApiFunction,
  moduleNameKey,
  callBackFn,
  conflictFn,
}: useHandleSubmitProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { displayMessage } = useSnackbar();

  const onSubmit: SubmitHandler<T> = useCallback(
    async (values) => {
      const moduleName = t(moduleNameKey);
      try {
        let response: ApiResponse;
        if (id) {
          response = await updateApiFunction?.(id, values);
        } else {
          response = await addApiFunction(values);
        }

        if (response.status === 200) {
          displayMessage(
            id
              ? t("common.apiMsgs.updateSuccess", { module: moduleName })
              : t("common.apiMsgs.createSuccess", { module: moduleName }),
            "success"
          );
          if (callBackFn) {
            callBackFn(response.data);
          } else {
            navigate(-1);
          }
        } else if (response.status === 401) {
          auth.signOut();
        } else if (response.status === 409) {
          conflictFn?.(response);
          displayMessage(response.data, "error");
        } else if (response.status === 429) {
          displayMessage(response.data, "error");
          conflictFn?.(response);
        } else {
          displayMessage(
            id
              ? t("common.apiMsgs.updateFailed", { module: moduleName })
              : t("common.apiMsgs.createFailed", { module: moduleName }),
            "error"
          );
        }
      } catch (error: any) {
        console.log(`${error.message}`, "error");
      }
    },
    [t, id, moduleNameKey, navigate, addApiFunction, updateApiFunction]
  );

  return onSubmit;
};

export default useHandleSubmit;
